import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ResultCard, ResultCardType } from "../../shared/interfaces/GameLogicTypes";
import ResultCardPanel from "../ResultCardPanel/ResultCardPanel";
import "./ResultCardGrid.css";

// Card types that have a visual element — these take priority over text-only cards when slicing to 9
const VISUAL_RESULT_TYPES = new Set([
  ResultCardType.WINNER, ResultCardType.CARRY,
  ResultCardType.BEST_ROUND, ResultCardType.WORST_ROUND, ResultCardType.PERFECT_ROUND,
  ResultCardType.HARDEST_QUESTION, ResultCardType.FULL_CONSENSUS, ResultCardType.FULL_INVERSION,
  ResultCardType.SOCIAL_GRAPH, ResultCardType.BUCKLE,
  ResultCardType.CONFIDENTLY_INCORRECT, ResultCardType.ONE_STEP_AHEAD,
  ResultCardType.FTL_FIRST_BLOOD,
]);

function sortResultCardsByVisualFirst(cards: ResultCard[]): ResultCard[] {
  const visual: ResultCard[] = [];
  const textOnly: ResultCard[] = [];
  for (const card of cards) {
    if (VISUAL_RESULT_TYPES.has(card.type)) visual.push(card);
    else textOnly.push(card);
  }
  return [...visual, ...textOnly];
}

/** The winner card is shown alone in the middle for this long before it flies to its slot.
 *  Long enough for the scoreboard bars inside it to finish growing. */
const HOLD_MS = 1450;
const POP_MS = 460;
const FLIGHT_MS = 620;

type RevealStage = "pop" | "fly" | "done";

interface Placement {
  /** Slot rect in viewport coords — the hero is parked here and transformed away from it. */
  top: number;
  left: number;
  width: number;
  height: number;
  /** Transform that lifts the hero from its slot to the middle of the screen, blown up. */
  centered: string;
}

function measure(el: HTMLElement): Placement {
  const rect = el.getBoundingClientRect();
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const size = Math.min(420, vw * 0.86, vh * 0.7);
  const scale = rect.width > 0 ? size / rect.width : 1;
  const dx = vw / 2 - (rect.left + rect.width / 2);
  const dy = vh / 2 - (rect.top + rect.height / 2);
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
    centered: `translate(${dx}px, ${dy}px) scale(${scale})`,
  };
}

export interface ResultCardGridProps {
  cards: ResultCard[];
  playerNames: string[];
}

/**
 * The end-of-game card grid. The winner plops in big in the middle of the screen, holds, then
 * physically flies down into its own slot in the grid while the rest of the cards pop up behind
 * it. The hero is a fixed-position copy parked exactly on the real slot, so when it lands with
 * `transform: none` it sits pixel-perfect on top of the tile it swaps with.
 */
export default function ResultCardGrid({ cards, playerNames }: ResultCardGridProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const ordered = useMemo(() => {
    const all = sortResultCardsByVisualFirst(cards);
    const galleries = all.filter((c) => c.type === ResultCardType.PICASSO_ROUND_GALLERY);
    const rest = all.filter((c) => c.type !== ResultCardType.PICASSO_ROUND_GALLERY).slice(0, 9);
    return [...rest, ...galleries];
  }, [cards]);

  const winnerIndex = ordered.findIndex((c) => c.type === ResultCardType.WINNER);
  const winnerCard = winnerIndex >= 0 ? ordered[winnerIndex] : undefined;

  const reducedMotion = useMemo(
    () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    []
  );

  const slotRef = useRef<HTMLDivElement | null>(null);
  const heroRef = useRef<HTMLDivElement | null>(null);
  const [placement, setPlacement] = useState<Placement | null>(null);
  const [stage, setStage] = useState<RevealStage>(winnerCard && !reducedMotion ? "pop" : "done");

  // Park the hero on the winner's slot and drive it out to the middle of the screen. Measuring
  // has to happen before paint, otherwise the hero flashes in the grid position for one frame.
  useLayoutEffect(() => {
    if (stage !== "pop" || !slotRef.current) return;
    setPlacement(measure(slotRef.current));
  }, [stage]);

  // Pop in: start small at the centre, then spring out to full size.
  useLayoutEffect(() => {
    const hero = heroRef.current;
    if (stage !== "pop" || !placement || !hero) return;
    hero.style.transition = "none";
    hero.style.opacity = "0";
    hero.style.transform = `${placement.centered} scale(0.35)`;
    const id = requestAnimationFrame(() => {
      hero.style.transition = `transform ${POP_MS}ms cubic-bezier(.2,.9,.3,1.5), opacity 200ms ease-out`;
      hero.style.opacity = "1";
      hero.style.transform = placement.centered;
    });
    return () => cancelAnimationFrame(id);
  }, [stage, placement]);

  // Hold, then fly home.
  useEffect(() => {
    if (stage !== "pop" || !placement) return;
    const id = setTimeout(() => setStage("fly"), POP_MS + HOLD_MS);
    return () => clearTimeout(id);
  }, [stage, placement]);

  useLayoutEffect(() => {
    const hero = heroRef.current;
    const slot = slotRef.current;
    if (stage !== "fly" || !hero || !slot) return;

    // Re-measure in case the page scrolled or resized while the winner was held. Applying the
    // fresh centred transform in the same frame keeps it visually still.
    const fresh = measure(slot);
    hero.style.transition = "none";
    hero.style.top = `${fresh.top}px`;
    hero.style.left = `${fresh.left}px`;
    hero.style.transform = fresh.centered;
    void hero.offsetHeight; // flush, so the transition below actually has a start value

    const id = requestAnimationFrame(() => {
      hero.style.transition = `transform ${FLIGHT_MS}ms cubic-bezier(.45,.02,.2,1)`;
      hero.style.transform = "none";
    });
    const done = setTimeout(() => setStage("done"), FLIGHT_MS + 40);
    return () => {
      cancelAnimationFrame(id);
      clearTimeout(done);
    };
  }, [stage]);

  const showHero = winnerCard && stage !== "done" && placement;

  return (
    <Box sx={{ width: "100%", maxWidth: "900px" }}>
      <Box
        sx={
          isMobile
            ? { display: "flex", flexDirection: "column", gap: 2 }
            : { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 2 }
        }
      >
        {ordered.map((card, i) => {
          const isWinner = i === winnerIndex;
          // Nothing but the winner exists until it starts flying home; the rest pop up behind it.
          const hidden = stage === "pop" || (isWinner && stage !== "done");
          return (
            <Box
              key={i}
              ref={isWinner ? slotRef : undefined}
              sx={{
                visibility: hidden ? "hidden" : "visible",
                animation:
                  stage === "fly" && !isWinner
                    ? `resultCardPop 380ms cubic-bezier(.2,.9,.3,1.4) both`
                    : "none",
                animationDelay: stage === "fly" && !isWinner ? `${i * 55}ms` : undefined,
              }}
            >
              <ResultCardPanel card={card} playerNames={playerNames} />
            </Box>
          );
        })}
      </Box>

      {showHero && (
        <Box
          ref={heroRef}
          onClick={() => setStage("fly")}
          sx={{
            position: "fixed",
            top: `${placement!.top}px`,
            left: `${placement!.left}px`,
            width: `${placement!.width}px`,
            height: `${placement!.height}px`,
            transformOrigin: "center center",
            zIndex: 1300,
            cursor: "pointer",
            willChange: "transform",
          }}
        >
          <ResultCardPanel card={winnerCard!} playerNames={playerNames} animateBars />
        </Box>
      )}
    </Box>
  );
}
